import { axios, transformAxiosError } from "../utils/axios.create";
import { Setting, SettingPayload } from "../domain/settingCatalog.interface";

export type SettingCatalogSearchPayload = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  application: string;
  type: string;
};

// Paginated search for the catalog table. Matches name/description only.
const search = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  application,
  type,
}: SettingCatalogSearchPayload): Promise<{
  list: Setting[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams({
    search,
    application,
    type,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  return axios
    .get(`/settings/catalog/search?${queryParams.toString()}`)
    .then((res) => ({
      list: res.data.body.list as Setting[],
      totalCount: res.data.body.totalCount as number,
    }))
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

// Bare array (excludes soft-deleted). Used to drive the per-account values
// screen, where every catalog definition needs a row.
const findAll = async (application?: string): Promise<Setting[]> => {
  const query = application
    ? `?${new URLSearchParams({ application }).toString()}`
    : "";

  return axios
    .get(`/settings/catalog${query}`)
    .then((res) => (res.data.body as Setting[]) || [])
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const findOne = async (id: string): Promise<Setting> => {
  return axios
    .get(`/settings/catalog/find/${id}`)
    .then((res) => res.data.body as Setting)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const createOne = async (payload: SettingPayload): Promise<Setting> => {
  return axios
    .post("/settings/catalog", payload)
    .then((res) => res.data.body as Setting)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

// PUT accepts partial fields. Returns the pre-update document.
const updateOne = async ({
  id,
  payload,
}: {
  id: string;
  payload: Partial<SettingPayload>;
}): Promise<Setting> => {
  return axios
    .put(`/settings/catalog/${id}`, payload)
    .then((res) => res.data.body as Setting)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const archiveOne = async (id: string): Promise<void> => {
  return axios
    .delete(`/settings/catalog/${id}`)
    .then(() => undefined)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const unarchiveOne = async (id: string): Promise<Setting> => {
  return axios
    .patch(`/settings/catalog/unarchive/${id}`)
    .then((res) => res.data.body as Setting)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export const SettingCatalogService = {
  search,
  findAll,
  findOne,
  createOne,
  updateOne,
  archiveOne,
  unarchiveOne,
};
